<script lang="ts">
  import { 
    X, Printer, ShieldCheck, Award, Droplets, CheckCircle2, 
    Building2, MapPin, User, Calendar, FileText
  } from 'lucide-svelte';

  interface Props {
    open?: boolean;
    onclose?: () => void;
    homeownerName?: string;
    propertyName?: string;
    propertyAddress?: string;
    installationDate?: string;
    valveDeviceEUI?: string;
    flowMeterEUI?: string;
    policyNumber?: string;
  }

  let { 
    open = true,
    onclose,
    homeownerName = 'Property Owner',
    propertyName = 'Primary Residence',
    propertyAddress = '100 South Grand Ave, Los Angeles, CA 90012',
    installationDate = 'March 15, 2026',
    valveDeviceEUI = '726c697a00001001',
    flowMeterEUI = '726c697a00001002',
    policyNumber = 'SY-INS-2026-88902'
  }: Props = $props();

  const currentDate = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  function printCertificate() {
    window.print();
  }
</script>

{#if open}
  <div class="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 print:p-0 print:bg-white print:static">
    <div class="bg-white rounded-3xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden border border-slate-200 print:shadow-none print:border-none print:max-w-none print:rounded-none">
      
      <!-- Top Action Bar (hidden in print) -->
      <div class="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/80 print:hidden">
        <div class="flex items-center gap-3">
          <div class="w-9 h-9 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center font-bold">
            <Award class="w-5 h-5" />
          </div>
          <div>
            <h3 class="font-bold text-slate-800 text-sm leading-tight">Homeowner Insurance Compliance Certificate</h3>
            <p class="text-xs text-slate-500">Official proof of automatic leak defense & smart shutoff valve</p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <!-- Print / PDF Button -->
          <button 
            class="px-3.5 py-1.5 bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all shadow-sm active:scale-95"
            onclick={printCertificate}
          >
            <Printer class="w-3.5 h-3.5" />
            Print / Save PDF
          </button>

          <!-- Close Modal -->
          <button 
            class="w-8 h-8 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 flex items-center justify-center transition-colors"
            onclick={() => props.onclose?.()}
          >
            <X class="w-4 h-4" />
          </button>
        </div>
      </div>

      <!-- Printable Certificate Sheet -->
      <div class="p-8 sm:p-12 text-slate-800 font-sans print:p-8 space-y-6 print:space-y-4 print:text-black relative border-8 border-double border-slate-200 m-2 sm:m-4 rounded-2xl print:border-slate-400">
        
        <!-- Watermark / Background Accent -->
        <div class="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03]">
          <ShieldCheck class="w-96 h-96 text-slate-900" />
        </div>

        <!-- Certificate Header -->
        <div class="text-center space-y-2 pb-6 border-b border-slate-200 relative z-10">
          <div class="inline-flex items-center gap-2 text-emerald-700 font-black tracking-widest text-xs uppercase bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200/60 mb-1">
            <ShieldCheck class="w-4 h-4 text-emerald-600" />
            Official Loss Prevention Certification
          </div>
          <h1 class="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight uppercase font-serif">
            Certificate of Automatic Leak Defense
          </h1>
          <p class="text-xs text-slate-500 max-w-lg mx-auto">
            Issued for Homeowner Property Insurance Premium Discount & Water Damage Mitigation Compliance
          </p>
        </div>

        <!-- Attestation Statement -->
        <div class="text-center text-xs text-slate-600 leading-relaxed max-w-xl mx-auto py-2 relative z-10">
          This document certifies that the residential property identified below is equipped with an active, 24/7 monitored <strong class="text-slate-900">Saya Intelligent Water Management System</strong> with automated motorized emergency shutoff capabilities.
        </div>

        <!-- Property & Insured Details -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50/80 p-5 rounded-2xl border border-slate-200 relative z-10 text-xs">
          <div class="space-y-2">
            <div>
              <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Insured Homeowner</span>
              <strong class="text-sm font-bold text-slate-900 flex items-center gap-1.5 mt-0.5">
                <User class="w-3.5 h-3.5 text-slate-500" />
                {homeownerName}
              </strong>
            </div>
            <div>
              <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Insured Property Address</span>
              <p class="text-slate-700 font-medium flex items-center gap-1.5 mt-0.5">
                <MapPin class="w-3.5 h-3.5 text-slate-500 shrink-0" />
                {propertyAddress}
              </p>
            </div>
          </div>

          <div class="space-y-2 sm:text-right">
            <div>
              <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Certificate ID</span>
              <span class="font-mono text-xs font-black text-slate-900">{policyNumber}</span>
            </div>
            <div>
              <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">System Certification Date</span>
              <span class="font-medium text-slate-700">{installationDate}</span>
            </div>
            <div>
              <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Verification Date</span>
              <span class="font-bold text-emerald-700">{currentDate}</span>
            </div>
          </div>
        </div>

        <!-- Hardware & Underwriting Specifications -->
        <div class="space-y-3 relative z-10">
          <h4 class="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
            <CheckCircle2 class="w-4 h-4 text-emerald-600" />
            Verified Loss-Prevention Features
          </h4>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div class="bg-white p-3 rounded-xl border border-slate-200 shadow-sm space-y-1">
              <span class="font-bold text-slate-900 block">Whole-Home Motorized Shutoff Valve</span>
              <p class="text-[11px] text-slate-500">
                Automated shutoff actuated in &lt;10 seconds upon detection of catastrophic pipe burst or continuous micro-leaks.
              </p>
              <div class="font-mono text-[10px] text-slate-400 pt-1">Device ID: {valveDeviceEUI}</div>
            </div>

            <div class="bg-white p-3 rounded-xl border border-slate-200 shadow-sm space-y-1">
              <span class="font-bold text-slate-900 block">Ultrasonic High-Precision Flow Sensor</span>
              <p class="text-[11px] text-slate-500">
                Class-A ultrasonic AMR sensor monitoring down to 0.01 GPM with temperature and pressure telemetry.
              </p>
              <div class="font-mono text-[10px] text-slate-400 pt-1">Device ID: {flowMeterEUI}</div>
            </div>

            <div class="bg-white p-3 rounded-xl border border-slate-200 shadow-sm space-y-1">
              <span class="font-bold text-slate-900 block">24/7 Cloud Autonomous Supervision</span>
              <p class="text-[11px] text-slate-500">
                Machine-learning behavioral baseline anomaly detection with away/vacation mode auto-arming.
              </p>
              <div class="text-[10px] text-emerald-600 font-semibold pt-1">Status: Active &amp; Online</div>
            </div>

            <div class="bg-white p-3 rounded-xl border border-slate-200 shadow-sm space-y-1">
              <span class="font-bold text-slate-900 block">Underwriting Standard Conformance</span>
              <p class="text-[11px] text-slate-500">
                Complies with IAPMO / ASME / FM-approved automatic water leak defense specifications for insurance discount credits.
              </p>
              <div class="text-[10px] text-slate-600 font-semibold pt-1">Eligible: 5%–15% Policy Premium Discount</div>
            </div>
          </div>
        </div>

        <!-- Sign-off & Remittance -->
        <div class="pt-6 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-end gap-6 relative z-10 text-xs">
          <div class="space-y-1 text-[11px] text-slate-500">
            <p class="font-bold text-slate-700">Insurance Carrier Instructions:</p>
            <p>
              Submit this certificate to your homeowner property insurance provider to verify installation of automatic water leak detection and shutoff equipment.
            </p>
            <div class="font-mono text-[10px] text-slate-400 pt-1">
              Saya Life Systems Inc. • Loss Prevention Engineering Division • support@saya.life
            </div>
          </div>

          <div class="text-right sm:min-w-[200px] border-t border-slate-900 pt-2 shrink-0">
            <div class="font-serif italic text-base font-bold text-slate-900">Dr. Ramin Rahbari</div>
            <div class="text-[10px] text-slate-500 font-medium uppercase tracking-wider">VP of IoT Engineering &amp; Operations</div>
            <div class="text-[10px] text-slate-400">Saya Life Systems Inc.</div>
          </div>
        </div>

      </div>

    </div>
  </div>
{/if}

<style>
  @media print {
    :global(body) {
      background: white !important;
      color: black !important;
    }
  }
</style>
